<template>
  <div class="article-tile" :class="tileClasses">
    <router-link :to="routerLinkTo" v-touch="touchHandler">
      <div class="background-image"
        :style="bgImageStyles"
        ref="articleImage"
      />
      <div class="info-container">
        <div class="info-content" ref="infoContent">
          <div class="logo" v-if="logoSrc">
            <img :src="logoSrc" />
          </div>
          <span class="headline">
            {{record.headline}}
          </span>
        </div>
      </div>
    </router-link>
  </div>
</template>

<script>
/*
  this component fits to the size of its container
  width: 100%; height: 100%;
  so container must define size and location
*/
// Libs
import Vue from 'vue';

import { Bus } from '@/helpers/bus';
import { Events } from '@/app-constants';
import { getResponsiveUrl } from '@/utils/image-utils';

import clients from '@/data/clients';

export default {

  props: ['record'],

  data() {
    return {
      isTapped: false,
      imageContentHeight: 0,
      imageContentWidth: 0,
      handleBusTapEventFunc: null,
    };
  },

  computed: {

    clientInfo() {
      const { client } = this.record;
      return clients[client] || {};
    },

    logoSrc() {
      const { logo } = this.clientInfo;
      return logo;
    },

    tileClasses() {
      const { isTapped } = this;
      return {
        tapped: isTapped,
      };
    },

    bgImageStyles() {
      const { imageContentHeight, imageContentWidth, record } = this;
      const { image } = record;
      const { src, hAlign: hA, vAlign: vA } = image;

      // add defaults
      const hAlign = hA || 'center';
      const vAlign = vA || 'center';

      const maxDim = Math.max(imageContentWidth, imageContentHeight);
      const rUrl = getResponsiveUrl(src, maxDim);

      const styles = {
        backgroundImage: `url(${rUrl})`,
        backgroundPosition: `${hAlign} ${vAlign}`,
      };
      return styles;
    },

    routerLinkTo() {
      const { url, data } = this.record;
      return {
        path: url,
        params: {
          url: url,
          data,
        },
      };
    },

    currentBrowserWidth() {
      return this.$store.state.width;
    },

  },

  methods: {

    touchHandler(e) {
      const { slug } = this.record;
      if (this.isTapped === true) {
        // double-tap: do default handling
        // console.warn(`double tap on ${slug}`);
      } else {
        // single-tap: show article content
        // console.warn(`single tap on ${slug}`);
        e.preventDefault();
        this.isTapped = true;
        Bus.$emit(Events.TappedArticle, slug);
      }
    },

    measureLayout() {
      this.imageContentHeight = this.getImageContentHeight();
      this.imageContentWidth = this.getImageContentWidth();
    },

    getImageContentHeight() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientHeight : 0;
    },

    getImageContentWidth() {
      const el = this.$refs.articleImage;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentBrowserWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
    this.handleBusTapEventFunc = (slug) => {
      const tapIsMe = (this.record.slug === slug);
      // console.log('tap event', tapIsMe, this.record.slug);
      if (!tapIsMe) {
        // reset any previous tapped state
        this.isTapped = false;
      }
    };
    Bus.$on(Events.TappedArticle, this.handleBusTapEventFunc);
  },

  beforeDestroy() {
    Bus.$off(Events.TappedArticle, this.handleBusTapEventFunc);
    this.handleBusTapEventFunc = null;
  },

};
</script>


<style lang="scss" scoped>
@import '~@/styles/_typography.scss';
@import '~@/styles/_variables.scss';
@import '~@/styles/_mixins.scss';

.article-tile {

  align-self: flex-start;

  width: 100%;
  height: 100%;

  > a {
    display: block;
    color: $color--primary-dark;

    width: 100%;
    height: 100%;
  }

  .background-image {
    position: absolute;
    background-size: cover;
    background-position: center center;
    width: 100%;
    height: 100%;
  }

  .info-container {
    width: 100%;
    height: 100%;

    background: rgba(white, $tile--default-opacity);

    opacity: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    .info-content {
      position: absolute;
      padding: rem(20px);

      // center the content
      top: 50%;
      transform: translateY(-50%);

      .logo {
        display: flex;
        flex-direction: row;
        justify-content: center;

        padding-bottom: rem(10px);

        > img {
          height: rem(60px);
        }
      }

      .headline {
        display: block;
        @include sansserif($size: (18, 24, 0), $weight: 'regular');
        color: black;
      }

    }
  }

  // animation
  .background-image {
    transition: all 250ms;
  }
  .info-container {
    transition: all 250ms;
  }
  .headline {
    transition: all 250ms;
    transform: translateY(60%);
  }
  .logo {
    transition: all 250ms;
    transform: translateY(-30%);
  }

  &:hover,
  &:active,
  &:focus,
  &.tapped {
    .info-container {
      opacity: 1;
    }

    .logo {
      transform: translateY(0);
    }

    .headline {
      transform: translateY(0);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/article-tile/ContentRevealTile.vue